import React, { useState, useEffect, use } from "react";
import { Check, Crown, Sparkles, AlertTriangle } from "lucide-react";
import axios from "axios";
import { useAlert } from "components/UI_components/Alert";
import { BASE_URL } from "utils/constant";
import SubscriptionCheckout from "./SubscriptionCheckout";

const SubscriptionModal = ({ isOpen, onClose }) => {
  const { addAlert } = useAlert();
  const [selectedPeriod, setSelectedPeriod] = useState("Monthly");
  const periods = ["Monthly", "Annually", "4 months", "6 months"];
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [userSubscription, setUserSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);

  // Map period names to expected API naming patterns
  const periodMapping = {
    Monthly: "Monthly Plan",
    Annually: "Yearly Plan",
    "4 months": "4 months",
    "6 months": "6 months",
  };

  // Fetch subscription plans from API
  useEffect(() => {
    const fetchSubscriptionPlans = async () => {
      try {
        setLoading(true);
        const accessToken = localStorage.getItem("access");
        const response = await axios.get(
          `${BASE_URL}/subscription/get-all-subscriptionplans/`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (response.data.status) {
          setSubscriptionPlans(response.data.data);
        } else {
          setError("Failed to load subscription plans");
        }
      } catch (err) {
        setError("Error connecting to subscription service");
        console.error("Subscription fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchSubscriptionPlans();
    }
  }, [isOpen]);
  useEffect(() => {
    const fetchUserSubscription = async () => {
      try {
        setLoading(true);
        const organization_id = JSON.parse(
          localStorage.getItem("organization")
        );
        const accessToken = localStorage.getItem("access");
        const response = await axios.post(
          `${BASE_URL}/subscription/get-user-subscription/`,
          {
            organization_id: organization_id.id,
          },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data.status) {
          setUserSubscription(response.data.data);
        } else {
          // setError("Failed to load user subscription");
        }
      } catch (err) {
        // setError("Error connecting to subscription service");
        console.error("Subscription fetch error:", err);
      }
    };

    if (isOpen) {
      fetchUserSubscription();
    }
  }, [isOpen]);

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  // Get the API plans filtered by period and feature type
  const getApiPlansForPeriod = (periodName, featureType) => {
    const periodPattern = periodMapping[periodName];

    return subscriptionPlans.filter((plan) => {
      const matchesPeriod = plan.subscription_name
        .toLowerCase()
        .includes(periodPattern.toLowerCase());
      const matchesFeature = plan.feature === featureType;
      return matchesPeriod && matchesFeature;
    });
  };

  // Free plan configuration - static since it doesn't come from the API
  const getFreePlan = () => ({
    name: "Free trial",
    price: null,
    planId: subscriptionPlans?.filter((plan) =>
      plan?.subscription_name.includes("Free")
    )[0]?.id,
    icon: Sparkles,
    buttonText: "Start 7 days free trial",
    buttonVariant: "outline",
    features: [
      "With ads",
      "Access organization dashboard",
      "Add upto 10 workers & 5 contractors",
      "Access only Inventory & Delivery tabs",
      "Materials: Add upto 10 materials",
      "Monthly reports: Download upto 2 reports",
    ],
  });

  // Build plan configuration based on API data and static frontend features
  const getPlansForPeriod = () => {
    // Get Basic plan from API
    const basicApiPlans = getApiPlansForPeriod(selectedPeriod, 2);
    const basicPlan = basicApiPlans.length > 0 ? basicApiPlans[0] : null;

    // Get Premium plan from API
    const premiumApiPlans = getApiPlansForPeriod(selectedPeriod, 3);
    const premiumPlan = premiumApiPlans.length > 0 ? premiumApiPlans[0] : null;

    const plans = [getFreePlan()];

    // Add Basic plan if available
    if (basicPlan) {
      plans.push({
        name: "Basic",
        price: basicPlan.total_charge.toString(), // Convert to display format
        savePercent: "17%",
        planId: basicPlan.id,
        razorpayPlanId: basicPlan.razorpay_subscription_plan_id,
        icon: Crown,
        buttonText: "Select plan",
        buttonVariant: "default",
        features: [
          "Ads-free",
          "Access organization dashboard",
          "Access all 3 tabs (Inventory/Delivery/Vendors)",
          "Add upto 50 workers & 25 contractors",
          "Excel import/export feature",
          "Members: Add upto 5 members",
          "Materials: Add upto 30 materials",
          "Vendors: Add upto 5 vendors",
          `Monthly reports: Download upto ${
            selectedPeriod === "Annually"
              ? "100"
              : selectedPeriod === "4 months"
              ? "45"
              : selectedPeriod === "6 months"
              ? "10"
              : "70"
          } reports`,
          `Customer support ticket: Raise upto ${
            selectedPeriod === "Annually"
              ? "25"
              : selectedPeriod === "4 months"
              ? "10"
              : selectedPeriod === "6 months"
              ? "2"
              : "10"
          } tickets with an average response time of 7 days & resolution time of 20 days`,
        ],
      });
    }

    // Add Premium plan if available
    if (premiumPlan) {
      plans.push({
        name: "Premium",
        price: premiumPlan.total_charge.toString(), // Convert to display format
        planId: premiumPlan.id,
        razorpayPlanId: premiumPlan.razorpay_subscription_plan_id,
        icon: Crown,
        buttonText: "Select plan",
        buttonVariant: "default",
        isBestValue: true,
        features: [
          "Ads-free",
          "Access organization dashboard",
          "Access all 3 tabs (Inventory/Delivery/Vendors)",
          "Add upto unlimited workers & contractors",
          "Excel import/export feature",
          "Members: Add upto 15 members",
          "Materials: Add unlimited materials",
          "Vendors: Add unlimited vendors",
          "Monthly reports: Download unlimited reports",
          `Customer support ticket: Raise upto ${
            selectedPeriod === "Annually"
              ? "30"
              : selectedPeriod === "4 months"
              ? "15"
              : selectedPeriod === "6 months"
              ? "4"
              : "20"
          } tickets with an average response time of 2 days & resolution time of 10 days`,
        ],
      });
    }

    return plans;
  };

  const startFreeTrial = async () => {
    try {
      setLoading(true);
      const accessToken = localStorage.getItem("access");
      const response = await axios.post(
        `${BASE_URL}/subscription/create-subscription/`,
        {
          subscription_plan_id: getFreePlan().planId,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.data.status) {
        setLoading(false);
        console.log("Free trial started successfully");
        addAlert("Free trial started successfully", "success");
        onClose();
      } else {
        setLoading(false);
        addAlert(response.data.error || "Error starting Free Trial", "error");
        console.error("Error starting free trial:", response.data.error);
      }
    } catch (err) {
      setLoading(false);
      addAlert(err || "Error starting Free Trial", "error");
      console.error("Error starting free trial:", err);
    }
  };

  // Handle plan selection
  const handleSelectPlan = (plan) => {
    // For free trial, you might have different logic
    if (!plan.price) {
      console.log("Starting free trial");
      startFreeTrial();
      return;
    }

    // For paid plans, proceed to checkout
    setSelectedPlan(plan);
    setShowCheckout(true);
  };

  // Handle back to plans
  const handleBackToPlans = () => {
    setShowCheckout(false);
    setSelectedPlan(null);
  };

  if (!isOpen) return null;

  // Show checkout screen if a plan is selected
  if (showCheckout && selectedPlan) {
    return (
      <SubscriptionCheckout
        selectedPlan={selectedPlan}
        onClose={()=>{
          onClose();
          setShowCheckout(false);
        }}
        onBack={handleBackToPlans}
      />
    );
  }
  const renderActiveSubscriptionWarning = () => {
    // Check if user has an active subscription
    if (
      userSubscription?.razorpay_subscription &&
      userSubscription?.active_flag === true &&
      new Date(userSubscription?.valid_till) > new Date()
    ) {
      return (
        <div className="mx-5 mb-8 bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle
            className="text-yellow-600 flex-shrink-0 mt-1"
            size={24}
          />
          <div>
            <p className="text-yellow-800 font-semibold mb-2">
              Active Subscription Detected
            </p>
            <p className="text-yellow-700 text-sm mb-3">
              You are currently using the{" "}
              <span className="font-bold">
                {userSubscription.subscription_name}
              </span>{" "}
              plan, valid until{" "}
              {new Date(userSubscription.valid_till).toLocaleDateString()}.
            </p>
            <p className="text-yellow-600 text-xs">
              <strong>Warning:</strong> If you purchase a new subscription, your
              current subscription will terminate immediately and cannot be
              undone.
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative w-full max-w-5xl bg-gradient-to-b from-purple-200 via-purple-100 to-white rounded-lg shadow-xl m-4 h-[99vh] overflow-auto">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-purple-100 transition-colors"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Header Section */}
        <div className="pt-8 pb-4 text-center">
          <h1 className="text-3xl font-bold text-purple-900 mb-2">
            Subscription plans
          </h1>
          <p className="text-base text-purple-700/80">
            Pick the plan that is right for you and subscribe to SiteMaster
          </p>
        </div>
        {renderActiveSubscriptionWarning()}
        {/* Period Selector */}
        <div className="flex justify-center mb-8">
          <div className="bg-white/80 backdrop-blur-sm rounded-full p-1 flex gap-1 shadow-lg">
            {periods.map((period) => (
              <button
                key={period}
                onClick={() => setSelectedPeriod(period)}
                className={`px-6 py-2 rounded-full transition-all text-sm font-medium ${
                  selectedPeriod === period
                    ? "bg-purple-600 text-white shadow-sm"
                    : "hover:bg-purple-50 text-purple-600"
                }`}
              >
                {period}
              </button>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-700"></div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="text-center py-16">
            <p className="text-red-500">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {/* Plans Grid */}
        {!loading && !error && (
  <div className="px-5 pb-10">
    <div className="grid md:grid-cols-2 gap-4">
      {getPlansForPeriod()
        .filter(plan => plan.name !== "Free trial")
        .map(plan => (
          <div
            key={plan.name}
            className={`bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-lg hover:shadow-xl transition-shadow relative ${
              plan.isBestValue ? "bg-gradient-to-b from-purple-50 to-white" : ""
            }`}
          >
            {plan.isBestValue && (
              <div className="absolute top-3 right-3 bg-yellow-400 text-xs font-bold px-3 py-0.5 rounded-full">
                Best value
              </div>
            )}
            {plan.savePercent && (
              <div className="absolute top-3 right-3 bg-purple-900 text-white text-xs font-bold px-3 py-0.5 rounded-full">
                Save {plan.savePercent}
              </div>
            )}
            <div className="flex items-center gap-2 mb-3">
              <plan.icon className="text-purple-600" size={20} />
              <h2 className="text-lg font-bold text-purple-900">
                {plan.name}
              </h2>
            </div>
            {plan.price ? (
              <div className="mb-4">
                <p className="text-2xl font-bold text-purple-900">
                  ₹ {plan.price}/-
                  <span className="text-xs font-normal text-purple-600 ml-1">
                    incl GST
                  </span>
                </p>
              </div>
            ) : (
              <div className="mb-4 h-8" /> // Spacer for free plan
            )}
            <button
              onClick={() => handleSelectPlan(plan)}
              className={`w-full ${
                plan.buttonVariant === "outline"
                  ? "bg-purple-100 text-purple-700 hover:bg-purple-200"
                  : "bg-purple-600 text-white hover:bg-purple-700"
              } py-2 rounded-lg text-sm font-medium mb-4 transition-colors`}
            >
              {plan.buttonText}
            </button>
            <div>
              <p className="text-xs text-purple-700 font-medium mb-2">
                {plan.price ? "Included in this plan" : "You will get"}
              </p>
              <div className="space-y-1.5">
                {plan.features.map(feature => (
                  <div key={feature} className="flex items-start gap-2">
                    <Check
                      size={14}
                      className="text-purple-600 mt-0.5 flex-shrink-0"
                    />
                    <span className="text-xs text-gray-600">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
    </div>
  </div>
)}

      </div>
    </div>
  );
};

export default SubscriptionModal;
